package com.merkost.handpass.admin.auth

import androidx.compose.runtime.*
import com.merkost.handpass.admin.api.AdminApiClient
import com.merkost.handpass.admin.api.fetchAdminRole
import com.merkost.handpass.admin.util.isDevPreview
import com.merkost.handpass.api.enums.AdminRole
import kotlinx.coroutines.launch

@Composable
fun AdminAuthGate(content: @Composable (AuthState.LoggedIn) -> Unit) {
    var authState by remember { mutableStateOf<AuthState>(AuthState.Loading) }
    var retryKey by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    DisposableEffect(Unit) {
        AdminApiClient.onUnauthorized = {
            scope.launch {
                runCatching { SupabaseAuthClient.signOut() }
                AdminApiClient.accessToken = null
                authState = AuthState.LoggedOut
            }
        }
        onDispose { AdminApiClient.onUnauthorized = null }
    }

    DisposableEffect(Unit) {
        if (isDevPreview()) return@DisposableEffect onDispose { }
        val unsubscribe = SupabaseAuthClient.onAuthStateChange { event, session ->
            when (event) {
                "SIGNED_OUT" -> {
                    AdminApiClient.accessToken = null
                    authState = AuthState.LoggedOut
                }
                "TOKEN_REFRESHED", "SIGNED_IN", "USER_UPDATED", "INITIAL_SESSION" ->
                    session?.let { AdminApiClient.accessToken = it.accessToken }
            }
        }
        onDispose { unsubscribe() }
    }

    LaunchedEffect(retryKey) {
        authState = AuthState.Loading
        if (isDevPreview()) {
            authState = AuthState.LoggedIn(
                AuthSession("preview", "preview", "preview", "preview@localhost"),
                AdminRole.SUPER_ADMIN,
            )
            return@LaunchedEffect
        }
        authState = try {
            val session = SupabaseAuthClient.getSession()
            if (session == null) AuthState.LoggedOut else resolveAdminState(session)
        } catch (e: Throwable) {
            AuthState.Error(e.message ?: "Couldn't verify your session. Check your connection and try again.")
        }
    }

    when (val state = authState) {
        AuthState.Loading -> LoadingScreen()
        AuthState.LoggedOut -> LoginPage(
            onLogin = { email, password ->
                scope.launch {
                    authState = AuthState.Loading
                    authState = try {
                        SupabaseAuthClient.signIn(email, password).fold(
                            onSuccess = { session -> resolveAdminState(session) },
                            onFailure = { AuthState.LoggedOut }
                        )
                    } catch (e: Throwable) {
                        AuthState.Error(e.message ?: "Sign-in failed. Please try again.")
                    }
                }
            },
            onGoogleSignIn = {
                scope.launch {
                    SupabaseAuthClient.signInWithGoogle()
                }
            }
        )
        is AuthState.Error -> ErrorScreen(
            message = state.message,
            onRetry = { retryKey++ },
            onLogout = {
                scope.launch {
                    SupabaseAuthClient.signOut()
                    AdminApiClient.accessToken = null
                    authState = AuthState.LoggedOut
                }
            }
        )
        is AuthState.NotAdmin -> AccessDeniedPage(
            email = state.email,
            onLogout = {
                scope.launch {
                    SupabaseAuthClient.signOut()
                    AdminApiClient.accessToken = null
                    authState = AuthState.LoggedOut
                }
            }
        )
        is AuthState.LoggedIn -> {
            IdleTimeout(timeoutMs = 30 * 60 * 1000) {
                scope.launch {
                    SupabaseAuthClient.signOut()
                    AdminApiClient.accessToken = null
                    authState = AuthState.LoggedOut
                }
            }
            content(state)
        }
    }
}

private suspend fun resolveAdminState(session: AuthSession): AuthState {
    AdminApiClient.accessToken = session.accessToken
    val role = fetchAdminRole()?.role
    return if (role != null) AuthState.LoggedIn(session, role) else AuthState.NotAdmin(session.email)
}
